/**
 * Shared Periodic Table UI Components
 * Consistent styling across the entire app
 */

import React from 'react';

// Get element color based on icon type
export const getElementColor = (iconName) => {
  const iconLower = iconName?.toLowerCase() || '';
  
  const colorMap = {
    'gift|airdrop': 'linear-gradient(135deg, #10B981 0%, #059669 30%, #047857 70%, #065F46 100%)',
    'users|user|holder': 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 30%, #1E7B34 70%, #145A25 100%)',
    'share|social|link': 'linear-gradient(135deg, #F59E0B 0%, #D97706 30%, #B45309 70%, #92400E 100%)',
    'whale|wallet': 'linear-gradient(135deg, #6366F1 0%, #4F46E5 30%, #4338CA 70%, #3730A3 100%)',
    'sniper|target|crosshair': 'linear-gradient(135deg, #EF4444 0%, #DC2626 30%, #B91C1C 70%, #991B1B 100%)',
    'health|monitor|activity': 'linear-gradient(135deg, #06B6D4 0%, #0891B2 30%, #0E7490 70%, #155E75 100%)',
    'timing|clock|oracle': 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 30%, #6D28D9 70%, #5B21B6 100%)',
    'pnl|profit|chart': 'linear-gradient(135deg, #10B981 0%, #059669 30%, #047857 70%, #065F46 100%)',
    'aging|age': 'linear-gradient(135deg, #F59E0B 0%, #D97706 30%, #B45309 70%, #92400E 100%)',
    'gas|fuel|price': 'linear-gradient(135deg, #FF6B35 0%, #E84125 30%, #D32F2F 70%, #B71C1C 100%)',
    'trend|intelligence|search': 'linear-gradient(135deg, #00E5FF 0%, #00D9FF 30%, #00ACC1 70%, #00838F 100%)',
    'sentiment|market': 'linear-gradient(135deg, #9945FF 0%, #7B2CBF 30%, #5A189A 70%, #3D0E61 100%)',
    'predictor|success': 'linear-gradient(135deg, #FFD700 0%, #FFC107 30%, #FFA000 70%, #FF8F00 100%)',
    'competition|scanner': 'linear-gradient(135deg, #EF4444 0%, #DC2626 30%, #B91C1C 70%, #991B1B 100%)',
    'influencer|tracker': 'linear-gradient(135deg, #EC4899 0%, #DB2777 30%, #BE185D 70%, #9F1239 100%)',
    'bot|volume': 'linear-gradient(135deg, #1E7B34 0%, #145A25 30%, #0F4A1F 70%, #0A3A18 100%)',
  };

  for (const [pattern, color] of Object.entries(colorMap)) {
    if (pattern.split('|').some(p => iconLower.includes(p))) return color;
  }
  
  return 'linear-gradient(135deg, #1E7B34 0%, #145A25 30%, #0F4A1F 70%, #0A3A18 100%)';
};

export const getHighlightColor = (iconName, direction = 'bottom') => {
  const iconLower = iconName?.toLowerCase() || '';
  const alpha = direction === 'right' ? '0.6' : '0.7';
  
  const colorMap = {
    'gift|airdrop': `rgba(16, 185, 129, ${alpha})`,
    'users|user|holder': `rgba(76, 175, 80, ${alpha})`,
    'share|social|link': `rgba(245, 158, 11, ${alpha})`,
    'whale|wallet': `rgba(99, 102, 241, ${alpha})`,
    'sniper|target|crosshair': `rgba(239, 68, 68, ${alpha})`,
    'health|monitor|activity': `rgba(6, 182, 212, ${alpha})`,
    'timing|clock|oracle': `rgba(139, 92, 246, ${alpha})`,
    'pnl|profit|chart': `rgba(16, 185, 129, ${alpha})`,
    'aging|age': `rgba(245, 158, 11, ${alpha})`,
    'gas|fuel|price': `rgba(255, 107, 53, ${alpha})`,
    'trend|intelligence|search': `rgba(0, 229, 255, ${alpha})`,
    'sentiment|market': `rgba(153, 69, 255, ${alpha})`,
    'predictor|success': `rgba(255, 215, 0, ${alpha})`,
    'competition|scanner': `rgba(239, 68, 68, ${alpha})`,
    'influencer|tracker': `rgba(236, 72, 153, ${alpha})`,
  };

  for (const [pattern, color] of Object.entries(colorMap)) {
    if (pattern.split('|').some(p => iconLower.includes(p))) return color;
  }
  
  return `rgba(129, 199, 132, ${alpha})`;
};

// Periodic Table Tile Component
export const PeriodicTile = ({
  icon: Icon,
  symbol,
  number,
  color,
  size = 39,
  onClick,
  disabled = false,
}) => {
  const bgColor = color || '#1E7B34';
  const highlightColor = `${bgColor}99`;
  
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '3px',
        background: typeof bgColor === 'string' && bgColor.includes('gradient')
          ? bgColor
          : `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 30%, ${bgColor}bb 70%, ${bgColor}99 100%)`,
        border: '0.5px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.3), 0 0 0 0.5px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'visible',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: onClick && !disabled ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* Top highlight */}
      <div style={{
        position: 'absolute',
        top: '1px',
        left: '1px',
        right: '1px',
        height: `${size * 0.36}px`,
        borderRadius: '2px 2px 0 0',
        background: `linear-gradient(to bottom, ${highlightColor} 0%, transparent 100%)`,
        pointerEvents: 'none',
        zIndex: 1,
      }} />
      {/* Left highlight */}
      <div style={{
        position: 'absolute',
        top: '1px',
        left: '1px',
        width: `${size * 0.36}px`,
        bottom: '1px',
        borderRadius: '2px 0 0 2px',
        background: `linear-gradient(to right, ${highlightColor} 0%, transparent 100%)`,
        pointerEvents: 'none',
        zIndex: 1,
      }} />
      {/* Bottom shadow */}
      <div style={{
        position: 'absolute',
        bottom: '1px',
        left: '1px',
        right: '1px',
        height: `${size * 0.26}px`,
        borderRadius: '0 0 2px 2px',
        background: 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.1) 60%, rgba(0, 0, 0, 0.25) 100%)',
        pointerEvents: 'none',
        zIndex: 2,
      }} />
      {/* Right shadow */}
      <div style={{
        position: 'absolute',
        top: '1px',
        right: '1px',
        width: `${size * 0.26}px`,
        bottom: '1px',
        borderRadius: '0 2px 2px 0',
        background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.08) 60%, rgba(0, 0, 0, 0.2) 100%)',
        pointerEvents: 'none',
        zIndex: 2,
      }} />
      {/* Atomic number */}
      {number && (
        <span style={{
          position: 'absolute',
          top: '3px',
          right: '4px',
          fontSize: `${size * 0.18}px`,
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.7)',
          zIndex: 15,
        }}>{number}</span>
      )}
      {/* Icon or Symbol */}
      {Icon ? (
        <Icon size={size * 0.46} style={{ color: '#fff', zIndex: 15, position: 'relative' }} />
      ) : symbol && (
        <span style={{
          fontSize: `${size * 0.4}px`,
          fontWeight: 700,
          color: '#fff',
          zIndex: 15,
          position: 'relative',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>{symbol}</span>
      )}
    </div>
  );
};

// Section Header - Periodic Table Style
export const SectionHeader = ({
  icon: Icon,
  title,
  symbol,
  number,
  color = '#1E7B34',
  onClick,
  isExpanded = true,
}) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      margin: '20px 0 12px 0',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <PeriodicTile icon={Icon} symbol={symbol} number={number} color={color} size={32} />
    <span style={{
      fontSize: '11px',
      fontWeight: 600,
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '1.2px',
    }}>
      {title}
    </span>
    <div style={{
      flex: 1,
      height: '1px',
      background: `linear-gradient(to right, ${color}66 0%, ${color}22 50%, transparent 100%)`,
      marginLeft: '8px',
    }} />
  </div>
);

// Tool Card Component - Periodic Table Style
export const ToolCard = ({
  icon: Icon,
  title,
  description,
  onClick,
  disabled,
  badge,
  badgeColor,
}) => {
  const iconName = Icon?.displayName || Icon?.name || 'Icon';
  const elementColor = getElementColor(iconName);
  const highlightColor = getHighlightColor(iconName);
  const highlightColorRight = getHighlightColor(iconName, 'right');
  
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0',
        minHeight: '40px',
        cursor: disabled ? 'not-allowed' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={(e) => !disabled && onClick && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)')}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
        {/* Periodic table style icon tile */}
        <div style={{
          width: '39px',
          height: '39px',
          borderRadius: '3px',
          background: elementColor,
          border: '0.5px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.3), 0 0 0 0.5px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          overflow: 'visible',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {/* Top highlight */}
          <div style={{
            position: 'absolute',
            top: '1px',
            left: '1px',
            right: '1px',
            height: '14px',
            borderRadius: '2px 2px 0 0',
            background: `linear-gradient(to bottom, ${highlightColor} 0%, transparent 100%)`,
            pointerEvents: 'none',
            zIndex: 1,
          }} />
          {/* Left highlight */}
          <div style={{
            position: 'absolute',
            top: '1px',
            left: '1px',
            width: '14px',
            bottom: '1px',
            borderRadius: '2px 0 0 2px',
            background: `linear-gradient(to right, ${highlightColorRight} 0%, transparent 100%)`,
            pointerEvents: 'none',
            zIndex: 1,
          }} />
          {/* Bottom shadow */}
          <div style={{
            position: 'absolute',
            bottom: '1px',
            left: '1px',
            right: '1px',
            height: '10px',
            borderRadius: '0 0 2px 2px',
            background: 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.1) 60%, rgba(0, 0, 0, 0.25) 100%)',
            pointerEvents: 'none',
            zIndex: 2,
          }} />
          {/* Right shadow */}
          <div style={{
            position: 'absolute',
            top: '1px',
            right: '1px',
            width: '10px',
            bottom: '1px',
            borderRadius: '0 2px 2px 0',
            background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.08) 60%, rgba(0, 0, 0, 0.2) 100%)',
            pointerEvents: 'none',
            zIndex: 2,
          }} />
          <Icon size={18} style={{ color: '#fff', zIndex: 10 }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--text-primary)',
            }}>
              {title}
            </span>
            {badge && (
              <span style={{
                fontSize: '8px',
                fontWeight: 700,
                padding: '2px 5px',
                borderRadius: '3px',
                background: badgeColor || 'var(--accent)',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                {badge}
              </span>
            )}
          </div>
          {description && (
            <span style={{
              fontSize: '11px',
              color: 'var(--text-muted)',
              display: 'block',
              marginTop: '2px',
            }}>
              {description}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Category Header - Periodic Table Style
export const CategoryHeader = ({
  icon: Icon,
  title,
  onClick,
  isExpanded = true,
}) => {
  const getElementInfo = (label) => {
    const map = {
      'basic': { symbol: 'Ba', number: '56' },
      'pro': { symbol: 'Pr', number: '59' },
      'max': { symbol: 'Mx', number: '115' },
      'intelligence': { symbol: 'In', number: '49' },
      'wallet': { symbol: 'Wa', number: '74' },
      'settings': { symbol: 'Se', number: '34' },
      'launch': { symbol: 'La', number: '57' },
      'tools': { symbol: 'Tl', number: '81' },
    };
    const key = Object.keys(map).find(k => label.toLowerCase().includes(k));
    return map[key] || { symbol: label.substring(0, 2), number: '?' };
  };
  
  const elementInfo = getElementInfo(title);
  const iconName = Icon?.displayName || Icon?.name || 'Icon';
  const elementColor = getElementColor(iconName);
  const highlightColor = getHighlightColor(iconName);
  const highlightColorRight = getHighlightColor(iconName, 'right');
  
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 0',
        marginTop: '16px',
        marginBottom: '6px',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {/* Periodic table style tile */}
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '3px',
        background: elementColor,
        border: '0.5px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {/* Top highlight */}
        <div style={{
          position: 'absolute',
          top: '1px',
          left: '1px',
          right: '1px',
          height: '12px',
          borderRadius: '2px 2px 0 0',
          background: `linear-gradient(to bottom, ${highlightColor} 0%, transparent 100%)`,
          pointerEvents: 'none',
        }} />
        {/* Left highlight */}
        <div style={{
          position: 'absolute',
          top: '1px',
          left: '1px',
          width: '12px',
          bottom: '1px',
          borderRadius: '2px 0 0 2px',
          background: `linear-gradient(to right, ${highlightColorRight} 0%, transparent 100%)`,
          pointerEvents: 'none',
        }} />
        {/* Atomic number */}
        <span style={{
          position: 'absolute',
          top: '2px',
          right: '3px',
          fontSize: '7px',
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.7)',
          zIndex: 10,
        }}>
          {elementInfo.number}
        </span>
        {/* Symbol */}
        <span style={{
          fontSize: '14px',
          fontWeight: 700,
          color: '#fff',
          zIndex: 10,
          marginTop: '2px',
          fontFamily: "'Bebas Neue', system-ui, sans-serif",
          letterSpacing: '1px',
        }}>
          {elementInfo.symbol}
        </span>
      </div>

      <span style={{
        fontSize: '11px',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '1.2px',
      }}>
        {title}
      </span>

      <div style={{
        flex: 1,
        height: '1px',
        background: 'linear-gradient(to right, rgba(46, 125, 50, 0.4) 0%, rgba(46, 125, 50, 0.1) 50%, transparent 100%)',
        marginLeft: '8px',
      }} />
    </div>
  );
};

// Stats Card - Periodic Table Style
export const StatsCard = ({
  icon: Icon,
  label,
  value,
  subValue,
  color = '#1E7B34',
  symbol,
  number,
}) => (
  <div style={{
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
  }}>
    <PeriodicTile icon={Icon} symbol={symbol} number={number} color={color} size={42} />
    <div>
      <div style={{
        fontSize: '18px',
        fontWeight: 700,
        color: 'var(--text-primary)',
        fontFamily: "'Bebas Neue', system-ui, sans-serif",
        letterSpacing: '1px',
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '10px',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {label}
      </div>
      {subValue && (
        <div style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>
          {subValue}
        </div>
      )}
    </div>
  </div>
);

// Toggle Switch - Periodic Table Style
export const ToggleSwitch = ({ enabled, onChange, color = '#1E7B34' }) => (
  <button
    onClick={() => onChange(!enabled)}
    style={{
      width: 44,
      height: 24,
      borderRadius: 4,
      border: '0.5px solid rgba(255, 255, 255, 0.2)',
      background: enabled
        ? `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`
        : 'linear-gradient(135deg, rgba(40,40,40,1) 0%, rgba(20,20,20,1) 100%)',
      cursor: 'pointer',
      position: 'relative',
      boxShadow: enabled ? `0 0 10px ${color}40` : '1px 2px 4px rgba(0,0,0,0.3)',
      overflow: 'hidden',
      padding: 0,
    }}
  >
    {enabled && (
      <div style={{
        position: 'absolute',
        top: 1,
        left: 1,
        right: 1,
        height: 8,
        borderRadius: '3px 3px 0 0',
        background: 'linear-gradient(to bottom, rgba(255,255,255,0.3) 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />
    )}
    <div style={{
      width: 18,
      height: 18,
      borderRadius: 3,
      background: enabled ? '#fff' : 'rgba(100,100,100,1)',
      position: 'absolute',
      top: 3,
      left: enabled ? 23 : 3,
      transition: 'all 0.2s ease',
      boxShadow: '1px 1px 3px rgba(0,0,0,0.3)',
    }} />
  </button>
);

export default {
  PeriodicTile,
  SectionHeader,
  ToolCard,
  CategoryHeader,
  StatsCard,
  ToggleSwitch,
  getElementColor,
  getHighlightColor,
};
